import { formatDistanceToNow } from 'date-fns';

export const TIMEZONE_COOKIE_NAME = 'lqd-timezone';

export type GreetingPeriod = 'morning' | 'afternoon' | 'evening';

export function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 100) {
    return false;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function getHourInTimeZone(date: Date, timeZone?: string): number {
  const hourPart = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hourCycle: 'h23',
    timeZone
  })
    .formatToParts(date)
    .find(part => part.type === 'hour');

  return Number(hourPart?.value ?? '0');
}

export function getGreetingPeriod(
  date: Date = new Date(),
  timeZone?: string
): GreetingPeriod {
  const hour = getHourInTimeZone(date, timeZone);
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

export function formatDateInTimeZone(
  date: Date,
  locale: Intl.LocalesArgument,
  options: Intl.DateTimeFormatOptions,
  timeZone?: string
): string {
  return date.toLocaleDateString(
    locale,
    timeZone ? { ...options, timeZone } : options
  );
}

export const formatTimeAgo = (date: Date | number, locale = 'en') => {
  const now = new Date();
  const elapsed =
    (now.getTime() - (date instanceof Date ? date.getTime() : date)) / 1000; // seconds
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const units: Array<{
    max: number;
    value: number;
    name: Intl.RelativeTimeFormatUnit;
  }> = [
    { max: 60, value: 1, name: 'second' },
    { max: 3600, value: 60, name: 'minute' },
    { max: 86400, value: 3600, name: 'hour' },
    { max: 2592000, value: 86400, name: 'day' },
    { max: 31536000, value: 2592000, name: 'month' },
    { max: Infinity, value: 31536000, name: 'year' }
  ];

  for (const unit of units) {
    if (elapsed < unit.max) {
      const value = Math.floor(elapsed / unit.value);
      return rtf.format(-value, unit.name);
    }
  }
};

export const formatTimeAgoInWords = (date: Date | number) => {
  return formatDistanceToNow(date, { addSuffix: true });
};
